import { fetchConfig, CartPerformance } from "./shopify.js";
import { PUB_SUB_EVENTS, defineElement } from "./gw.js";
import { subscribe, publish } from "./pubsub.js";
class CartDiscount extends HTMLElement {
  cartUpdateUnsubscriber;
  handleSubmit = async (event) => {
    var _a;
    event.preventDefault();
    event.stopPropagation();
    if (!this.input || !this.input.value.trim()) return;
    const discountCode = this.input.value.trim();
    const existingDiscounts = this.getExistingDiscounts();
    if (existingDiscounts.includes(discountCode)) {
      this.input.value = "";
      return;
    }
    this.hideError();
    this.setLoading(true);
    const abortController = this.createAbortController();
    try {
      const updatedDiscounts = [...existingDiscounts, discountCode];
      const body = JSON.stringify({
        discount: updatedDiscounts.join(","),
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body,
        signal: abortController.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const rejectedDiscount =
        (_a = data.discount_codes) == null
          ? void 0
          : _a.find(
              (discount) =>
                discount.code === discountCode && discount.applicable === false,
            );
      if (rejectedDiscount) {
        this.showError();
        this.input.value = "";
        return;
      }
      this.input.value = "";
      this.updateSections(data);
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "cart-discount",
        cartData: data,
      });
      CartPerformance.measureFromEvent("discount-apply:user-action", event);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error applying discount:", error);
        this.showError();
      }
    } finally {
      this.setLoading(false);
      this.activeFetch = null;
    }
  };
  constructor() {
    super();
    this.errorElement = this.querySelector("[data-cart-discount-error]");
    this.form = this.querySelector("form");
    this.input = this.querySelector('input[name="discount"]');
    if (this.form) {
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
    }
    this.addEventListener("click", this.handleRemoveClick.bind(this));
    this.addEventListener("keydown", this.handleRemoveKeydown.bind(this));
    this.activeFetch = null;
  }
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-discount") {
          return;
        }
        this.onCartUpdate();
      },
    );
  }
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.activeFetch) {
      this.activeFetch.abort();
    }
  }
  createAbortController() {
    if (this.activeFetch) {
      this.activeFetch.abort();
    }
    this.activeFetch = new AbortController();
    return this.activeFetch;
  }
  /**
   * Handle click events for remove buttons
   * @param {MouseEvent} event
   */
  handleRemoveClick(event) {
    const removeButton = event.target.closest("[data-remove-discount]");
    if (!removeButton) return;
    event.preventDefault();
    this.removeDiscount(removeButton.dataset.removeDiscount, event);
  }
  /**
   * Handle keyboard events for remove buttons
   * @param {KeyboardEvent} event
   */
  handleRemoveKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    const removeButton = event.target.closest("[data-remove-discount]");
    if (!removeButton) return;
    event.preventDefault();
    this.removeDiscount(removeButton.dataset.removeDiscount, event);
  }
  /**
   * Remove a discount code
   * @param {string} discountCode
   * @param {Event} event
   */
  async removeDiscount(discountCode, event) {
    if (!discountCode) return;
    const existingDiscounts = this.getExistingDiscounts();
    const updatedDiscounts = existingDiscounts.filter(
      (code) => code !== discountCode,
    );
    this.setLoading(true);
    const abortController = this.createAbortController();
    try {
      const body = JSON.stringify({
        discount: updatedDiscounts.join(","),
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body,
        signal: abortController.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      this.updateSections(data);
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "cart-discount",
        cartData: data,
      });
      CartPerformance.measureFromEvent("discount-remove:user-action", event);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error removing discount:", error);
      }
    } finally {
      this.setLoading(false);
      this.activeFetch = null;
    }
  }
  /**
   * Get existing discount codes from the DOM
   * @returns {string[]}
   */
  getExistingDiscounts() {
    const discountElements = this.querySelectorAll("[data-discount-code]");
    return Array.from(discountElements)
      .map((el) => el.dataset.discountCode)
      .filter(Boolean);
  }
  /**
   * Get sections that need to be re-rendered
   * @returns {Array}
   */
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
  /**
   * Update page sections with new content
   * @param {Object} data
   */
  updateSections(data) {
    this.getSectionsToRender().forEach((section) => {
      var _a;
      const sectionElement = document.getElementById(section.id);
      const targetElement =
        (sectionElement == null
          ? void 0
          : sectionElement.querySelector(section.selector)) || sectionElement;
      if (
        targetElement &&
        ((_a = data.sections) == null ? void 0 : _a[section.section])
      ) {
        const newContent = this.getSectionInnerHTML(
          data.sections[section.section],
          section.selector,
        );
        targetElement.innerHTML = newContent;
      }
    });
  }
  /**
   * Extract inner HTML from section response
   * @param {string} html
   * @param {string} selector
   * @returns {string}
   */
  getSectionInnerHTML(html, selector) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : html;
  }
  /**
   * Handle cart updates from other components
   */
  onCartUpdate() {
    var _a, _b;
    const sectionId =
      (_b = (_a = this.closest(".shopify-section")) == null ? void 0 : _a.id) ==
      null
        ? void 0
        : _b.replace("shopify-section-", "");
    if (!sectionId) return;
    fetch(`${routes.cart_url}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newElement = doc.querySelector("cart-discount");
        if (newElement && this.parentNode) {
          this.parentNode.replaceChild(newElement, this);
        }
      })
      .catch((error) => {
        console.error("Error updating cart discount section:", error);
      });
  }
  /**
   * Show error message
   */
  showError() {
    if (this.errorElement) {
      this.errorElement.classList.remove("hidden");
      this.errorElement.setAttribute("aria-hidden", "false");
    }
  }
  /**
   * Hide error message
   */
  hideError() {
    if (this.errorElement) {
      this.errorElement.classList.add("hidden");
      this.errorElement.setAttribute("aria-hidden", "true");
    }
  }
  /**
   * Set loading state
   * @param {boolean} loading
   */
  setLoading(loading) {
    var _a;
    const submitButton =
      (_a = this.form) == null
        ? void 0
        : _a.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = loading;
      submitButton.classList.toggle("loading", loading);
    }
    if (this.input) {
      this.input.disabled = loading;
    }
  }
}
defineElement("cart-discount", CartDiscount);
export { CartDiscount };
